import React, {
  useCallback,
  createElement,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
} from 'react';

import TablePaginationActions from './mtable-pagination-actions';
import TableToolbar from './mtable-toolbar';
import {
  Column,
  usePagination,
  useRowSelect,
  useSortBy,
  useFilters,
  useTable,
  useAsyncDebounce,
} from 'react-table';
import { useSubTable } from './useSubTable';
import {
  Table as MaUTable,
  Checkbox,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  LinearProgress,
  Collapse,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { BsFillInboxesFill } from '@icon/react-icons';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { useClearAllSelectedRows } from './use-clear-options';
import { useRefreshCounter } from '../search/c3-search';

export const EnhancedTable = ({
  columns,
  data,
  updateData = null,
  pageCount,
  rowCount,
  initial = null,
  getSubRows,
  loading = false,
  getRowProps = row => ({}),
}) => {
  const { refreshCounter } = useRefreshCounter();
  const hiddenColumns = useMemo(() => columns.filter(c => c.forceHide).map(c => c.id), [columns]);
  const classes = useRowStyles();
  const {
    getTableProps,getTableBodyProps,
    headerGroups,
    prepareRow,
    toggleRowSelected,
    page,
    gotoPage,
    setAllFilters,
    setPageSize,
    setFilter,
    clearAllSelectedRows,
    allColumns,
    state: { pageIndex, pageSize, selectedRowIds, filters, sortBy },
  } = useTable(
    {
      columns,
      data,
       pageCount,
      defaultColumn: defaultColumn as any,
      getRowId,
      //renderSubTable: getSubRows,
      manualPagination: true,
      manualFilters: true,
      manualSortBy: true,  
      initialState: {  pageIndex: 0,pageSize: 15, ...initial, hiddenColumns },
      autoResetHiddenColumns: false,
      autoResetSelectedRows: false,
      autoResetPage: true,
    },
    useFilters,
    useSortBy,
    //useSubTable,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.allColumns.push(columns => [
        // Let's make a column for selection
        {
          id: 'selection',
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox.  Pagination is a problem since this will select all
          // rows even though not all rows are on the current page.  The solution should
          // be server side pagination.  For one, the clients should not download all
          // rows in most cases.  The client should only download data for the current page.
          // In that case, getToggleAllRowsSelectedProps works fine.
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
        },
        ...columns,
      ]);
    },
    useClearAllSelectedRows
  );

  const tableContainerRef = useRef<HTMLDivElement>();
  const [maxHeight, setMaxHeight] = useState<string>();
  const updateHeight = useAsyncDebounce(() => {
    if (tableContainerRef.current) {
      setMaxHeight(`calc(100vh - ${tableContainerRef.current.getBoundingClientRect().top + 20}px)`);
      tableContainerRef.current.scrollTo(0, 0);
    }
  });

  useEffect(() => {
    updateHeight();
  }, [data]);

  const updateDataDebounced = useAsyncDebounce((...args) => updateData && updateData(...args), 300);


  useEffect(() => {
   if (updateDataDebounced) {
    updateDataDebounced({ pageSize, pageIndex, filters, sortBy }, gotoPage);
  }

  }, [pageSize, pageIndex, filters, sortBy, refreshCounter]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      gotoPage(newPage);
    },
    [gotoPage]
  );

  const handleChangeRowsPerPage = useCallback(
    event => {
      setPageSize(Number(event.target.value));
    },
    [setPageSize]
  );

  const rowPerPageOptions = useMemo(() => [ 10, 25, 50, 100], []);
  
  // Render the UI for your table
  return (
    <>
      <TableToolbar
        numSelected={Object.keys(selectedRowIds).length}
        selectedRows={selectedRowIds}
        columns={allColumns}
        setFilter={setFilter}
        setAllFilters={setAllFilters}
        filters={filters}
        clearAllSelectedRows={clearAllSelectedRows}
      />
      {loading ? <LinearProgress variant="query" /> : <div style={{ height: 4 }} />}
      <StyledTableContainer ref={tableContainerRef} style={{ maxHeight }}>
        <MaUTable {...getTableProps()} stickyHeader size="small">
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <TableCell
                    {...(column.id === 'selection'
                      ? column.getHeaderProps({
                          style: {
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          },
                        })
                      : column.getHeaderProps({
                          style: {
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          },
                        }))}>
                    <div style={{ display: 'flex', lineHeight: 'normal' }}>
                      {column.render('Header')}
                      {!['selection', 'showtable'].includes(column.id) && column.canSort ? (
                        <TableSortLabel
                          {...column.getSortByToggleProps()}
                          active={column.isSorted}
                          // react-table has a unsorted state which is not treated here
                          direction={column.isSortedDesc ? 'desc' : 'asc'}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page.length ? (
              
              page.map(row => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps([
                  { className: classes.root },
                  getRowProps(row),
                ]);
                return (
                  <React.Fragment key={key}>
                    <TableRow {...rowProps}>
                      {row.cells.map(cell => {
                        return (
                          <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                        );
                      })}
                    </TableRow>
                    {/* <TableRow key={`${row.id}-extra`}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={row.cells.length}>
                        <Collapse
                          enter={false}
                          exit={false}
                          in={(row as any).isSubTableOpen}
                          unmountOnExit>
                          {(row as any).isSubTableOpen &&
                            createElement((row as any).renderSubTable)}
                        </Collapse>
                        {/* <div style={{ display: (row as any).isSubTableOpen ? 'block' : 'none' }}>
                          {(row as any).isSubTableOpen &&
                            createElement((row as any).renderSubTable)}
                        </div> 
                      </TableCell>
                    </TableRow> */}
                  </React.Fragment>
                );
              })
            ) : (
              <EmptyRows />
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={rowPerPageOptions}
                count={rowCount}
                rowsPerPage={pageSize}
                page={pageIndex}
                SelectProps={SelectProps}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        </MaUTable>
      </StyledTableContainer>
    </>
  );
};

const StyledTableContainer = styled(TableContainer)`
  ${thumbStack}
`;

const IndeterminateCheckbox = React.forwardRef<
  HTMLInputElement,
  { indeterminate: boolean } & Record<string, any>
>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    // @ts-ignore
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Checkbox inputRef={resolvedRef} indeterminate={indeterminate} {...rest} />;
});

const defaultColumn: Partial<Column> = {
  Filter: () => null,
};

const EmptyRows = styled(({ className }) => (
  <tr className={className}>
    <td colSpan={100}>
      <BsFillInboxesFill color="disabled" />
      <br />
      <Typography color="textSecondary">No Data</Typography>
    </td>
  </tr>
))`
  td {
    padding: 95px;
    text-align: center;
  }
`;

function getRowId(row) {
  return row.vtr_id;
}

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
      backgroundColor: 'white',
    },
  },
});

const SelectProps = {
  inputProps: { 'aria-label': 'rows per page' },
  native: true,
};
